import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { zodResolver } from '@hookform/resolvers/zod';
import CompanyHeader from '@/components/company/CompanyHeader';
import FormBuilder from '@/components/company/FormBuilder';
import type { FormField } from '@/components/company/FormBuilder';
import GenerateAIModal from '@/components/company/GenerateAIModal';
import AIResultModal from '@/components/company/AIResultModal';
import VisionAIGenerateForm from './vision/VisionAIGenerateForm';
import useCompanyState from '@/states/company.state';
import useTenantState from '@/states/tenant.state';
import { useGenerateVision } from './vision/useGenerateVision';
import { visionAIGenerateSchema, type VisionAIGenerateFormData } from './vision/vision-ai-generate.schema';

interface VisionFormValues {
  vision: string;
  is_published_vision: boolean;
}

export default function Vision() {
  const formControl = useForm<VisionFormValues>({
    defaultValues: {
      vision: '',
      is_published_vision: false,
    },
  });

  const { handleSubmit, reset, watch, setValue } = formControl;
  const formRef = useRef<HTMLFormElement>(null);

  const aiFormControl = useForm<VisionAIGenerateFormData>({
    resolver: zodResolver(visionAIGenerateSchema),
    defaultValues: {
      future_aspiration: '',
      target_achievement: '',
      desired_impact: '',
      timeline: '',
    },
  });

  const { companyProfile, updateCompanyField, isLoading } = useCompanyState();
  const { currentTenant } = useTenantState();
  const isPublished = watch('is_published_vision');
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [generatedOptions, setGeneratedOptions] = useState<string[]>([]);

  const generateVisionMutation = useGenerateVision({
    tenantId: currentTenant?.id ? String(currentTenant.id) : '',
  });

  useEffect(() => {
    if (!companyProfile) return;

    const visionValue =
      typeof companyProfile.vision === 'object' ? companyProfile.vision?.value : companyProfile.vision;

    const publishedValue =
      typeof companyProfile.vision === 'object'
        ? companyProfile.vision?.is_published
        : companyProfile.is_published_vision;

    reset({ vision: visionValue ?? '', is_published_vision: !!publishedValue });
  }, [companyProfile, reset]);

  const handleSave = async (data: VisionFormValues) => {
    if (!currentTenant?.id) return;

    updateCompanyField(currentTenant.id, 'vision', data.vision, data.is_published_vision);
  };

  const handleOpenGenerateModal = () => {
    aiFormControl.reset();
    setIsGenerateModalOpen(true);
  };

  const handleCloseGenerateModal = () => {
    setIsGenerateModalOpen(false);
  };

  const handleGenerate = async (data: VisionAIGenerateFormData) => {
    try {
      const result = await generateVisionMutation.mutateAsync(data);

      if (result.success && result.data?.generated_options) {
        setGeneratedOptions(result.data.generated_options);
        setIsGenerateModalOpen(false);
        setIsResultModalOpen(true);
        toast.success('Vision generated successfully');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate vision');
    }
  };

  const handleCloseResultModal = () => {
    setIsResultModalOpen(false);
    setGeneratedOptions([]);
  };

  const handleSaveGeneratedVision = (selectedOption: string) => {
    setValue('vision', selectedOption);
    setIsResultModalOpen(false);
    setGeneratedOptions([]);
  };

  const handleAfterSave = async () => {
    if (!currentTenant?.id) return;
    const currentVision = formControl.getValues('vision');
    const currentPublished = formControl.getValues('is_published_vision');
    await updateCompanyField(currentTenant.id, 'vision', currentVision, currentPublished);
    toast.success('Vision saved successfully');
  };

  const fields: FormField[] = [
    {
      name: 'vision',
      label: 'Company Vision',
      type: 'markdown',
      required: true,
      placeholder: "Enter your company's vision statement...",
    },
  ];

  return (
    <section className="w-full">
      <CompanyHeader
        title="Vision Statement"
        description="Define your company's long-term vision and aspirations for the future."
        onGenerate={handleOpenGenerateModal}
        generateLabel="Generate"
        saveLabel="Save"
        onSave={() => formRef.current?.requestSubmit()}
        isSaving={isLoading}
      />

      <div className="mx-5 border-b border-gray-200" />

      <FormProvider {...formControl}>
        <form ref={formRef} onSubmit={handleSubmit(handleSave)} className="pt-5">
          <FormBuilder
            fields={fields}
            initialPublished={!!isPublished}
            onPublishChange={val => setValue('is_published_vision', val)}
          />
        </form>
      </FormProvider>

      <GenerateAIModal
        isOpen={isGenerateModalOpen}
        onClose={handleCloseGenerateModal}
        onSubmit={aiFormControl.handleSubmit(handleGenerate)}
        title="Generate Vision with AI"
        description="Provide the information below to help AI generate vision that suits your company."
        generateButtonLabel="Generate Vision"
        isLoading={generateVisionMutation.isLoading}
      >
        <VisionAIGenerateForm
          control={aiFormControl.control}
          errors={aiFormControl.formState.errors}
          isLoading={generateVisionMutation.isLoading}
        />
      </GenerateAIModal>

      <AIResultModal
        isOpen={isResultModalOpen}
        onClose={handleCloseResultModal}
        options={generatedOptions}
        onSave={handleSaveGeneratedVision}
        onAfterSave={handleAfterSave}
        title="AI Generated Vision"
        description="Select the vision that best represents your company"
        saveButtonLabel="Save to Profile"
        isLoading={isLoading}
      />
    </section>
  );
}
